/**
 * @fileoverview Sticky header — transparent/solid swap, dropdown keyboard support.
 *
 * On the home page the header renders transparent over the first section and fades
 * to the solid style once you scroll past it. An IntersectionObserver watches that
 * first section with its root inset from the top by exactly the header's height, so
 * the swap fires as the section's bottom edge passes under the header — no scroll
 * listener and no hardcoded threshold (the hero's height is merchant-configurable).
 *
 * A custom element rather than a DOMContentLoaded handler: the theme editor swaps
 * the section's HTML wholesale, and the element lifecycle tears the observers down
 * and rebuilds them for free.
 *
 * Hide-on-scroll: the header no longer sits permanently over the page. Scrolling
 * down slides it away; scrolling up brings it back. The wrapper stays sticky the
 * whole time — only the inner element translates — so the transparent/solid IO
 * logic above is untouched. The current occupied height is published as
 * `--header-reveal-height` (header height when visible, 0 when hidden) so fixed
 * elements that dock beneath the header (assets/album-sticky-bar.css) can follow
 * the reveal without their own scroll listeners. Hiding is suppressed in the
 * theme editor, near the top of the page, and while a dropdown, the mobile menu,
 * or keyboard focus holds the header open.
 */

const DESKTOP_BREAKPOINT = 990;

/** Accumulated scroll (px) in one direction before the header reacts — small
 *  enough to feel immediate, large enough not to flicker on trackpad noise. */
const SCROLL_HIDE_DELTA = 12;

class StickyHeader extends HTMLElement {
  /** True on the home page — the only place the transparent style applies. */
  #overlay = false;
  /** Scrolled past the section the header overlays. */
  #pastFirst = false;
  /** A dropdown is open — the header goes solid so nav and panel read as one surface. */
  #dropdownOpen = false;

  #height = 0;
  /** @type {IntersectionObserver|null} */
  #io = null;
  /** @type {ResizeObserver|null} */
  #ro = null;
  #rafId = 0;

  /** Slid out of view by the hide-on-scroll behaviour. */
  #hidden = false;
  #lastY = 0;
  /** Signed accumulated scroll since the last direction change. */
  #scrollAccum = 0;
  #scrollRafId = 0;

  #boundSyncDropdown = null;
  #boundKeydown = null;
  #boundFocusIn = null;
  #boundFocusOut = null;
  /** True while the merchant has this header's section selected in the theme
   *  editor — the one editor state where auto-hide would fight the person
   *  actively editing the thing. Everywhere else the editor scrolls and hides
   *  exactly like the storefront, so what the merchant sees is what ships. */
  #editorSelected = false;
  #boundSectionSelect = null;
  #boundSectionDeselect = null;
  #boundSectionChange = null;
  #boundResize = null;
  #boundScroll = null;

  connectedCallback() {
    this.#overlay = this.hasAttribute('data-overlay');

    this.#boundSyncDropdown = () => this.#syncDropdown();
    this.#boundKeydown = (e) => this.#onKeydown(e);
    this.#boundFocusIn = (e) => this.#onFocusIn(e);
    this.#boundFocusOut = (e) => this.#onFocusOut(e);
    this.#boundSectionChange = () => this.#observeTarget();
    this.#boundResize = () => this.#closeMobileMenuOnDesktop();

    // dropdown:open / dropdown:close bubble out of <dropdown-element>. Binding both to
    // one handler that re-queries the DOM keeps this order-independent, so hovering
    // from one parent item straight to another doesn't flicker.
    this.addEventListener('dropdown:open', this.#boundSyncDropdown);
    this.addEventListener('dropdown:close', this.#boundSyncDropdown);
    this.addEventListener('keydown', this.#boundKeydown);
    this.addEventListener('focusin', this.#boundFocusIn);
    this.addEventListener('focusout', this.#boundFocusOut);

    // Catches the 990px breakpoint and font-swap reflow, which a resize listener misses.
    this.#ro = new ResizeObserver(() => this.#onResize());
    this.#ro.observe(this);

    document.addEventListener('shopify:section:load', this.#boundSectionChange);
    document.addEventListener('shopify:section:unload', this.#boundSectionChange);
    document.addEventListener('shopify:section:reorder', this.#boundSectionChange);
    window.addEventListener('resize', this.#boundResize);

    // Editor parity: scroll-hide behaves exactly as on the storefront, except
    // while this header's own section is selected — then it stays put so the
    // merchant can see what they are editing.
    this.#boundSectionSelect = (e) => {
      if (!e.target.contains(this)) return;
      this.#editorSelected = true;
      this.#setHidden(false);
    };
    this.#boundSectionDeselect = (e) => {
      if (e.target.contains(this)) this.#editorSelected = false;
    };
    document.addEventListener('shopify:section:select', this.#boundSectionSelect);
    document.addEventListener('shopify:section:deselect', this.#boundSectionDeselect);

    this.#boundScroll = () => this.#onScroll();
    this.#lastY = window.scrollY;
    window.addEventListener('scroll', this.#boundScroll, { passive: true });

    this.#measure();
    this.#observeTarget();
    this.#render();
    this.#applyHidden();
  }

  disconnectedCallback() {
    this.#io?.disconnect();
    this.#ro?.disconnect();
    cancelAnimationFrame(this.#rafId);
    cancelAnimationFrame(this.#scrollRafId);
    window.removeEventListener('scroll', this.#boundScroll);

    this.removeEventListener('dropdown:open', this.#boundSyncDropdown);
    this.removeEventListener('dropdown:close', this.#boundSyncDropdown);
    this.removeEventListener('keydown', this.#boundKeydown);
    this.removeEventListener('focusin', this.#boundFocusIn);
    this.removeEventListener('focusout', this.#boundFocusOut);

    document.removeEventListener('shopify:section:load', this.#boundSectionChange);
    document.removeEventListener('shopify:section:unload', this.#boundSectionChange);
    document.removeEventListener('shopify:section:reorder', this.#boundSectionChange);
    document.removeEventListener('shopify:section:select', this.#boundSectionSelect);
    document.removeEventListener('shopify:section:deselect', this.#boundSectionDeselect);
    window.removeEventListener('resize', this.#boundResize);
  }

  /**
   * Publishes the measured height so #main-content's negative margin and the
   * observer's rootMargin stay in sync with reality. Nothing inside the header may
   * size itself from this var, or we'd loop.
   */
  #measure() {
    const height = this.getBoundingClientRect().height;
    if (!height || height === this.#height) return false;

    this.#height = height;
    document.documentElement.style.setProperty('--header-height', `${Math.round(height)}px`);
    this.#publishRevealHeight();
    return true;
  }

  /* ----- Hide on scroll down, reveal on scroll up ----- */

  #onScroll() {
    cancelAnimationFrame(this.#scrollRafId);
    this.#scrollRafId = requestAnimationFrame(() => {
      const y = Math.max(0, window.scrollY);
      const delta = y - this.#lastY;
      this.#lastY = y;

      // Direction change resets the accumulator so a long slow scroll in one
      // direction still triggers, but jitter around a point never does.
      this.#scrollAccum = Math.sign(delta) === Math.sign(this.#scrollAccum)
        ? this.#scrollAccum + delta
        : delta;

      if (this.#mustStayVisible() || y <= this.#height) {
        this.#setHidden(false);
        return;
      }

      if (this.#scrollAccum > SCROLL_HIDE_DELTA) this.#setHidden(true);
      else if (this.#scrollAccum < -SCROLL_HIDE_DELTA) this.#setHidden(false);
    });
  }

  /** States where hiding the header would strand the visitor. In the theme
   *  editor the header scrolls and hides exactly like the storefront (editor
   *  parity); it holds still only while its own section is selected. */
  #mustStayVisible() {
    if (this.#editorSelected) return true;
    if (this.#dropdownOpen) return true;
    if (this.contains(document.activeElement)) return true;
    return Boolean(document.getElementById('mobile-menu')?.hasAttribute('open'));
  }

  #setHidden(hidden) {
    if (hidden === this.#hidden) return;
    this.#hidden = hidden;
    this.#applyHidden();
  }

  #applyHidden() {
    this.classList.toggle('header--hidden', this.#hidden);
    // pointer-events lives on the sticky wrapper: with the header slid away the
    // wrapper still spans a header-sized strip at the top of the viewport and
    // would otherwise eat clicks on whatever scrolled underneath it.
    this.closest('.header-section')?.classList.toggle('header-section--hidden', this.#hidden);
    this.#publishRevealHeight();
  }

  /** Height the header currently occupies at the top of the viewport — what
   *  docked fixed elements (album sticky bar) offset themselves by. */
  #publishRevealHeight() {
    document.documentElement.style.setProperty(
      '--header-reveal-height',
      this.#hidden ? '0px' : `${Math.round(this.#height)}px`
    );
  }

  #onResize() {
    cancelAnimationFrame(this.#rafId);
    this.#rafId = requestAnimationFrame(() => {
      // rootMargin is baked in at construction, so a height change means a new observer.
      if (this.#measure()) this.#observeTarget();
    });
  }

  /** The section the header overlays — whatever is first inside <main>. */
  #observeTarget() {
    this.#io?.disconnect();
    this.#io = null;

    if (!this.#overlay) return;

    const target = document.getElementById('main-content')?.firstElementChild;
    if (!target) {
      // Nothing to overlay — stay solid rather than risk white-on-white.
      this.#pastFirst = true;
      this.#render();
      return;
    }

    this.#io = new IntersectionObserver(
      (entries) => {
        this.#pastFirst = !entries[0].isIntersecting;
        this.#render();
      },
      {
        rootMargin: `-${Math.round(this.#height)}px 0px 0px 0px`,
        threshold: 0,
      }
    );

    this.#io.observe(target);
  }

  #syncDropdown() {
    this.#dropdownOpen = Boolean(this.querySelector('dropdown-element[open]'));
    if (this.#dropdownOpen) this.#setHidden(false);

    this.querySelectorAll('.header__nav-link[aria-haspopup]').forEach((link) => {
      const dropdown = link.closest('.header__nav-item')?.querySelector('dropdown-element');
      link.setAttribute('aria-expanded', dropdown?.hasAttribute('open') ? 'true' : 'false');
    });

    this.#render();
  }

  #render() {
    const solid = this.#pastFirst || this.#dropdownOpen;
    this.classList.toggle('header--transparent', this.#overlay && !solid);
  }

  /* ----- Keyboard support: <mega-menu-trigger> is mouseenter-only ----- */

  #onFocusIn(event) {
    // Keyboard users tabbing into the header must be able to see it.
    this.#setHidden(false);

    const container = this.querySelector('mega-menu-container');
    if (!container) return;

    const trigger = event.target.closest('mega-menu-trigger');
    if (trigger) {
      container.openDropdown?.(trigger.getAttribute('dropdown-id'));
      return;
    }

    // Focus moved into an open panel — leave it open.
    if (event.target.closest('dropdown-element')) return;

    container.closeActive?.();
  }

  #onFocusOut(event) {
    const container = this.querySelector('mega-menu-container');
    if (container && !container.contains(event.relatedTarget)) container.closeActive?.();
  }

  #onKeydown(event) {
    if (event.key !== 'Escape') return;

    // Runs before DropdownElement's own document-level Escape handler, so the panel
    // is still open and we can hand focus back to its trigger rather than stranding
    // it on an element that is about to be hidden.
    const open = this.querySelector('dropdown-element[open]');
    open?.closest('.header__nav-item')?.querySelector('.header__nav-link')?.focus();
  }

  #closeMobileMenuOnDesktop() {
    if (window.innerWidth <= DESKTOP_BREAKPOINT) return;

    const mobileMenu = document.getElementById('mobile-menu');
    if (mobileMenu?.hasAttribute('open')) {
      window.dispatchEvent(new Event('close-drawer-mobile-menu'));
    }
  }
}

if (!customElements.get('sticky-header')) {
  customElements.define('sticky-header', StickyHeader);
}
